import { useCallback, useEffect, useMemo, useState } from "react";
import { calledFromEnums, commonFieldOptionEnums, fieldNameEnums } from "../../components/pages/apiPage/apiPageHelper";
import { valueDropdownData } from "../../config/apiPageData";
import * as Library from "../../utils/Library";
import { useNavigate } from "react-router";

const createEmptyStep = () => ({
    id: Date.now(),
    type: "",
    url: "",
    auth: "",
    returnType: "",
    fields: []
});
const mapFieldsRecursive = (fields = [], category, calledFrom, getDropdownOptions = () => []) => {
    return fields.map(field => ({
        id: Date.now() + Math.random(),
        category,
        type: field.type || "String",
        name: field.name || "",
        value:
            field.connectFieldName ||
            field.extraFieldName ||
            field.returnFieldName ||
            (field.predefinedValue && commonFieldOptionEnums.PREDEFINED) ||
            "",
        predefinedValue: field.predefinedValue || "",
        connectFieldName: field.connectFieldName || "",
        extraFieldName: field.extraFieldName || "",
        returnFieldName: field.returnFieldName || "",
        options: getDropdownOptions(category, calledFrom, field.predefinedValue),
        arrayFields: mapFieldsRecursive(field.arrayFields || [], category, calledFrom)
    }));
};

const convertFieldRecursive = field => {
    const baseField = {
        name: field.name,
        type: field.type
    };

    if (field.predefinedValue) baseField.predefinedValue = field.predefinedValue;
    if (field.extraFieldName) baseField.extraFieldName = field.extraFieldName;
    if (field.connectFieldName) baseField.connectFieldName = field.connectFieldName;
    if (field.returnFieldName) baseField.returnFieldName = field.returnFieldName;

    if (field.arrayFields?.length > 0) {
        baseField.arrayFields = field.arrayFields.map(convertFieldRecursive);
    }

    return baseField;
};

export default function useApiSteps({ connectedFieldOptions, endpointOptions, apiStepsData }) {
    const [steps, setSteps] = useState([createEmptyStep()]);
    const [stepsWithAuth, setStepsWithAuth] = useState([]);
    const [popupDropDown, setPopupDropDown] = useState([]);

    const addStep = () => {
        setSteps(prev => [...prev, createEmptyStep()]);
    };

    const addField = (stepIndex, category, calledFrom) => {
        setSteps(prev => {
            const updated = [...prev];
            const step = updated[stepIndex];
            step.fields.push({
                id: Date.now(),
                category,
                type: "String",
                name: "",
                value: "",
                predefinedValue: "",
                options: getDropdownOptions(category, calledFrom),
                connectFieldName: "",
                extraFieldName: "",
                returnFieldName: ""
            });
            return updated;
        });
    };
    const updateArraySubField = (stepIndex, parentFieldId, subFieldId, key, value) => {
        const updateRecursive = fields =>
            fields.map(field => {
                if (field.id === parentFieldId) {
                    return {
                        ...field,
                        arrayFields: (field.arrayFields || []).map(subField => {
                            if (subField.id === subFieldId) {
                                let updatedSubField = { ...subField };

                                if (key === "value" && value === "Predefined") {
                                    updatedSubField = {
                                        ...updatedSubField,
                                        value: "Predefined"
                                    };
                                } else if (key === "value") {
                                    updatedSubField = {
                                        ...updatedSubField,
                                        value
                                    };
                                } else if (key === fieldNameEnums.CONNECTED_FIELD) {
                                    updatedSubField = {
                                        ...updatedSubField,
                                        connectFieldName: value
                                    };
                                } else if (key === fieldNameEnums.EXTRA_FIELD) {
                                    updatedSubField = {
                                        ...updatedSubField,
                                        extraFieldName: value
                                    };
                                } else if (key === fieldNameEnums.RETURN_FIELD) {
                                    updatedSubField = {
                                        ...updatedSubField,
                                        returnFieldName: value
                                    };
                                } else {
                                    updatedSubField = { ...updatedSubField, [key]: value };
                                }

                                if (
                                    key === "type" &&
                                    value === "Array Fields" &&
                                    (!updatedSubField.arrayFields || updatedSubField.arrayFields.length === 0)
                                ) {
                                    updatedSubField.arrayFields = [
                                        {
                                            id: Date.now() + Math.random(),
                                            type: "",
                                            name: "",
                                            value: "",
                                            predefinedValue: "",
                                            connectFieldName: "",
                                            extraFieldName: "",
                                            returnFieldName: ""
                                        }
                                    ];
                                }

                                return updatedSubField;
                            }

                            if (subField.arrayFields && subField.arrayFields.length > 0) {
                                return {
                                    ...subField,
                                    arrayFields: updateRecursive(subField.arrayFields)
                                };
                            }

                            return subField;
                        })
                    };
                }

                if (field.arrayFields && field.arrayFields.length > 0) {
                    return {
                        ...field,
                        arrayFields: updateRecursive(field.arrayFields)
                    };
                }

                return field;
            });

        setSteps(prevSteps =>
            prevSteps.map((step, sIndex) =>
                sIndex === stepIndex ? { ...step, fields: updateRecursive(step.fields) } : step
            )
        );
    };

    const removeArraySubField = (stepIndex, parentFieldId, subFieldId) => {
        const removeRecursive = fields => {
            return fields.map(field => {
                if (field.id === parentFieldId) {
                    return {
                        ...field,
                        arrayFields: (field.arrayFields || []).filter(f => f.id !== subFieldId)
                    };
                }

                if (field.arrayFields && field.arrayFields.length > 0) {
                    return {
                        ...field,
                        arrayFields: removeRecursive(field.arrayFields)
                    };
                }

                return field;
            });
        };

        setSteps(prevSteps =>
            prevSteps.map((step, sIndex) =>
                sIndex === stepIndex ? { ...step, fields: removeRecursive(step.fields) } : step
            )
        );
    };

    const updateField = (stepIndex, fieldId, key, value, dynamicKey = "", isValueDropdown, arrayFieldIndex = null) => {
        setSteps(prev => {
            const updated = [...prev];
            const step = updated[stepIndex];
            const field = step.fields.find(f => f.id === fieldId);

            if (field) {
                if (arrayFieldIndex === null) {
                    field[key] = value;

                    if (isValueDropdown) {
                        field.connectFieldName = "";
                        field.extraFieldName = "";
                        field.predefinedValue = "";
                        field.returnFieldName = "";
                    }

                    if (dynamicKey) {
                        field[dynamicKey] = value;
                    }

                    if (key === "value") {
                        field.selected = !!value;
                    }

                    if (
                        key === "type" &&
                        value === "Array Fields" &&
                        (!field.arrayFields || field.arrayFields.length === 0)
                    ) {
                        field.arrayFields = [
                            {
                                id: Date.now() + Math.random(),
                                type: "",
                                name: "",
                                value: "",
                                connectFieldName: "",
                                extraFieldName: "",
                                returnedFieldName: ""
                            }
                        ];
                    }
                } else {
                    const arrayField = field.arrayFields[arrayFieldIndex];
                    if (arrayField) {
                        arrayField[key] = value;
                        if (dynamicKey) {
                            arrayField[dynamicKey] = value;
                        }
                    }
                }
            }

            return updated;
        });
    };
    const updatePopField = (stepIndex, key, value, id) => {
        setStepsWithAuth(prev => {
            const updated = [...prev];
            const step = updated[stepIndex];
            const field = step.fields.find(item => item.id === id);

            if (field) {
                field[key] = undefined;
                field[key] = value;

                if (key) {
                    field[key] = value;
                }
                if (key === "value") {
                    field.selected = !!value;
                }
            }

            return updated;
        });
    };

    const getFieldsByCategory = (step, category) => {
        return step.fields.filter(f => f.category === category);
    };

    const removeStep = stepIndex => {
        setSteps(prev => prev.filter((_, i) => i !== stepIndex));
    };

    const removeField = (stepIndex, fieldId) => {
        setSteps(prev => {
            const updated = [...prev];
            updated[stepIndex].fields = updated[stepIndex].fields.filter(field => field.id !== fieldId);
            return updated;
        });
    };

    const updateStepField = (stepIndex, key, value) => {
        setSteps(prev => {
            const updated = [...prev];
            updated[stepIndex][key] = value;
            return updated;
        });
    };

    const setStepsFromApiData = apiSteps => {
        const mappedSteps = apiSteps.map(apiStep => {
            const step = {
                id: Date.now() + Math.random(),
                type: apiStep.urlMethodType || "",
                url: apiStep.url || "",
                auth: apiStep.authorizationName || "",
                returnType: apiStep.returnType || "",
                fields: []
            };

            step.fields = [
                ...mapFieldsRecursive(
                    apiStep.headerFields || [],
                    "headers",
                    calledFromEnums.API_PAGE,
                    getDropdownOptions
                ),
                ...mapFieldsRecursive(
                    apiStep.paramsFields || [],
                    "params",
                    calledFromEnums.API_PAGE,
                    getDropdownOptions
                ),
                ...mapFieldsRecursive(apiStep.bodyFields || [], "body", calledFromEnums.API_PAGE, getDropdownOptions),
                ...mapFieldsRecursive(
                    apiStep.returnedFields || [],
                    "returnFields",
                    calledFromEnums.API_PAGE,
                    getDropdownOptions
                )
            ];

            return step;
        });

        setSteps(mappedSteps.length > 0 ? mappedSteps : [createEmptyStep()]);
    };

    const getDropdownOptions = (category, calledFrom, predefinedValue) => {
        const commonOptions = [
            {
                name: commonFieldOptionEnums.PREDEFINED,
                value: commonFieldOptionEnums.PREDEFINED,
                selected: predefinedValue ? true : false
            },
            { name: commonFieldOptionEnums.CONNECTED, value: commonFieldOptionEnums.CONNECTED }
        ];

        if (calledFrom === calledFromEnums.API_PAGE && category !== "returnFields") {
            return [...(endpointOptions?.inputFields || []), ...connectedFieldOptions, ...commonOptions];
        } else if (calledFrom === calledFromEnums.AUTH_POPUP) {
            return [...connectedFieldOptions, ...commonOptions];
        } else if (calledFrom === calledFromEnums.API_PAGE && category === "returnFields") {
            return [...(endpointOptions?.outputFields || []), ...connectedFieldOptions, ...commonOptions];
        } else {
            return commonOptions;
        }
    };

    const getFilteredSteps = ({ steps }) => {
        return steps?.map(step => {
            const stepFields = {
                headerFields: [],
                paramsFields: [],
                bodyFields: [],
                returnedFields: []
            };

            step?.fields?.forEach(field => {
                const baseField = convertFieldRecursive(field);

                switch (field.category) {
                    case "headers":
                        stepFields.headerFields.push(baseField);
                        break;
                    case "params":
                        stepFields.paramsFields.push(baseField);
                        break;
                    case "body":
                        stepFields.bodyFields.push(baseField);
                        break;
                    case "returnFields":
                        stepFields.returnedFields.push(baseField);
                        break;
                    default:
                        break;
                }
            });

            const result = {
                urlMethodType: step.type,
                url: step.url,
                authorizationName: step.auth,
                returnedDataStructure: step.returnType,
                status: "Incomplete"
            };

            Object.entries(stepFields).forEach(([key, value]) => {
                if (value.length > 0) {
                    result[key] = value;
                }
            });

            return result;
        });
    };

    const getEndpointFields = endpointName => {
        const matched = valueDropdownData.find(item => item.endPoint === endpointName);
        return {
            inputFields: matched?.inputFields || [],
            outputFields: matched?.outputFields || []
        };
    };
    const useFormValidation = (steps = [], isAuthValidation) => {
        return useMemo(() => {
            if (!Array.isArray(steps)) return false;

            const validateField = field => {
                const {
                    type,
                    name,
                    value,
                    connectFieldName,
                    predefinedValue,
                    extraFieldName,
                    arrayFields,
                    returnFieldName
                } = field;
                const hasAnyValue =
                    !!value || !!connectFieldName || !!predefinedValue || !!extraFieldName || !!returnFieldName;

                if (value === commonFieldOptionEnums.PREDEFINED && !predefinedValue) return false;
                if (!(type && name && hasAnyValue)) return false;

                if (type === "Array Fields" && Array.isArray(arrayFields) && arrayFields.length > 0) {
                    return arrayFields.every(subField => validateField(subField));
                }

                return true;
            };

            return steps.every(({ auth, returnType, type, url, fields = [] }) => {
                if (!fields.length && !isAuthValidation) {
                    return !!(auth && returnType && type && url);
                }
                return fields.every(field => validateField(field));
            });
        }, [steps, isAuthValidation]);
    };

    useEffect(() => {
        setSteps(prev =>
            prev.map(step => ({
                ...step,
                fields: step.fields.map(field => ({
                    ...field,
                    options: getDropdownOptions(field.category, calledFromEnums.API_PAGE)
                }))
            }))
        );
    }, [connectedFieldOptions]);

    useEffect(() => {
        if (apiStepsData?.length > 0) {
            setStepsFromApiData(apiStepsData || []);
        }
    }, [apiStepsData]);
    const useEndpointDataValidation = () => {
        const navigate = useNavigate();

        const validate = useCallback(
            endpointName => {
                if (!endpointName || typeof endpointName !== "string") return false;

                const endpoint = valueDropdownData.find(e => e.endPoint === endpointName);
                if (!endpoint) {
                    Library.showToastMessage({
                        type: "error",
                        title: `Endpoint "${endpointName}" is not configured in frontend.`
                    });
                    return false;
                }

                const { inputFields = [], outputFields = [] } = endpoint;

                const checkFields = (fields, fieldType) => {
                    for (let field of fields) {
                        const { value, isMandatory, isRecommended } = field || {};
                        if (isMandatory && isRecommended) {
                            Library.showToastMessage({
                                type: "error",
                                title: `[${endpointName}] "${value}" in ${fieldType} cannot be both mandatory and recommended.`
                            });
                            navigate(-1);
                            return false;
                        }
                    }
                    return true;
                };

                const inputOk = checkFields(inputFields, "inputFields");
                if (!inputOk) return false;

                const outputOk = checkFields(outputFields, "outputFields");
                if (!outputOk) return false;

                return true;
            },
            [navigate]
        );

        return { validate };
    };
    const extractFieldsFromAuth = ({ authorizationFieldsData = [], connectedFieldOptions = [] }) => {
        return ({ category, key }) =>
            authorizationFieldsData
                ?.flatMap(item => item[key])
                .map(item => {
                    const findConnectedFieldValue = connectedFieldOptions?.find(
                        connectedField => connectedField?.name === item?.connectFieldName
                    );
                    return {
                        ...item,
                        id: `${Date.now() + Math.random()}`,
                        category,
                        predefinedValue: findConnectedFieldValue?.predefinedValue || ""
                    };
                });
    };
    const mergeStepsWithAuthData = ({ steps, authorizationFieldsData = [], connectedFieldOptions }) => {
        let dropDownData = [];
        const extraAuthFields = extractFieldsFromAuth({ authorizationFieldsData, connectedFieldOptions });
        const bodyFields = extraAuthFields({
            category: "body",
            key: "bodyFields"
        });

        const headerFields = extraAuthFields({
            category: "headers",
            key: "headerFields"
        });

        const paramsFields = extraAuthFields({
            category: "params",
            key: "paramsFields"
        });
        const mergedFields = [...bodyFields, ...headerFields, ...paramsFields];
        const stepsWithAuth = steps?.map(step => {
            const newObj = {
                ...step,
                fields: [...(step?.fields || []), ...mergedFields]
            };
            dropDownData = newObj.fields.map(field => ({
                name: field?.connectFieldName || field?.value || field?.extraFieldName,
                value: field?.value || field?.extraFieldName || field?.connectFieldName
            }));

            return newObj;
        });
        setPopupDropDown(dropDownData);
        setStepsWithAuth(stepsWithAuth);
    };

    const extractFields = (steps = []) => {
        const extraFields = {};
        const connectFields = {};

        steps.forEach(step => {
            step.fields?.forEach(field => {
                const values = field.fieldValue || field.returnFieldName || field.value || field.predefinedValue || "";

                if (field.extraFieldName) {
                    extraFields[field.extraFieldName] = values
                        .split(",")
                        .map(v => v.trim())
                        .filter(Boolean);
                }

                if (field.connectFieldName) {
                    connectFields[field.connectFieldName] = values
                        .split(",")
                        .map(v => v.trim())
                        .filter(Boolean);
                }
            });
        });

        return { extraFields, connectFields };
    };

    const testFormValidation = (steps = []) => {
        return steps.every(step => {
            const relevantFields = step?.fields?.filter(
                field => field?.category !== "returnFields" && !field?.predefinedValue
            );
            return relevantFields.every(f => f.fieldValue);
        });
    };

    const resetStepsFieldValues = () => {
        setSteps(prev =>
            prev.map(step => ({
                ...step,
                fields: step.fields.map(field => ({
                    ...field,
                    fieldValue: ""
                }))
            }))
        );
    };

    return {
        steps,
        addStep,
        addField,
        updateField,
        getFieldsByCategory,
        removeStep,
        removeField,
        updateStepField,
        getFilteredSteps,
        getEndpointFields,
        useFormValidation,
        useEndpointDataValidation,
        extractFields,
        testFormValidation,
        resetStepsFieldValues,
        mergeStepsWithAuthData,
        popupDropDown,
        stepsWithAuth,
        updatePopField,
        updateArraySubField,
        removeArraySubField
    };
}
